import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAdStatus, selectAdViewInProgress, recordAdView, checkAdStatus } from '../../../../app/store/slices/userDashboardSlice';
import { FiLock, FiUnlock, FiClock, FiPlayCircle, FiX } from 'react-icons/fi';
import { FiAward } from 'react-icons/fi';
import { useCanvas } from '../../../dashboard/context/CanvasContext';
import GoogleAd from '../ads/GoogleAd';

const DEFAULT_CLICK_URL = process.env.REACT_APP_DEFAULT_AD_CLICK_URL || 'https://www.mycashcanvas.com';
const VIEW_SECONDS_REQUIRED = 10;
const SAMPLE_ADS = [
  { id: 'ad-main', slotId: process.env.REACT_APP_AD_SLOT_MAIN, clickUrl: DEFAULT_CLICK_URL },
  { id: 'ad-sidebar', slotId: process.env.REACT_APP_AD_SLOT_SIDEBAR, clickUrl: DEFAULT_CLICK_URL },
].filter(ad => ad.slotId);

const AdStatusWidget = ({ onUnlock }) => {
  const dispatch = useDispatch();
  const adStatus = useSelector(selectAdStatus);
  const adViewInProgress = useSelector(selectAdViewInProgress);

  const [showAdModal, setShowAdModal] = useState(false);
  const [currentAd, setCurrentAd] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [adWatchTime, setAdWatchTime] = useState(0);
  const [adLoaded, setAdLoaded] = useState(false);
  const [adRecorded, setAdRecorded] = useState(false);
  const { selectedCanvas } = useCanvas();

  useEffect(() => {
    if (adStatus?.hasAccess && selectedCanvas?.id) {
      const pollInterval = setInterval(() => {
        dispatch(checkAdStatus(selectedCanvas.id));
      }, 10000);
      return () => clearInterval(pollInterval);
    }
  }, [adStatus?.hasAccess, dispatch, selectedCanvas?.id]);

  useEffect(() => {
    if (adStatus?.hasAccess && showAdModal) {
      setShowAdModal(false);
      setCurrentAd(null);
      setAdWatchTime(0);
      setAdLoaded(false);
      setAdRecorded(false);
      setIsProcessing(false);
      if (onUnlock) onUnlock();
    }
  }, [adStatus?.hasAccess, showAdModal, onUnlock]);

  // Track watch time
  useEffect(() => {
    if (!showAdModal || !currentAd || !adLoaded || adRecorded) return;

    const timer = setInterval(() => {
      setAdWatchTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [showAdModal, currentAd, adLoaded, adRecorded]);

  // Auto-complete when user watches for 30 seconds
  useEffect(() => {
    if (showAdModal && adLoaded && !adRecorded && adWatchTime >= VIEW_SECONDS_REQUIRED) {
      handleAdComplete('completed');
    }
  }, [adWatchTime, showAdModal, adLoaded, adRecorded]);

  const handleWatchAdsClick = () => {
    const pool = SAMPLE_ADS.length ? SAMPLE_ADS : [{ id: 'ad-fallback', slotId: process.env.REACT_APP_AD_SLOT_MAIN, clickUrl: DEFAULT_CLICK_URL }];
    const randomAd = pool[Math.floor(Math.random() * pool.length)];
    setCurrentAd(randomAd);
    setShowAdModal(true);
    setAdWatchTime(0);
    setAdLoaded(false);
    setAdRecorded(false);
  };

  const handleAdComplete = (completionStatus = 'completed') => {
    if (!currentAd || isProcessing || adRecorded) {
      console.warn('[AdWidget] Ad completion blocked:', { currentAd: !!currentAd, isProcessing, adRecorded });
      return;
    }
    
    if (!selectedCanvas?.id) {
      console.error('[AdWidget] Canvas ID missing, cannot record ad view');
      return;
    }
    
    setIsProcessing(true);
    setAdRecorded(true);
        
    dispatch(recordAdView({
      adId: currentAd.id,
      status: completionStatus,
      durationWatched: adWatchTime,
      selectedCanvasId: selectedCanvas?.id
    }))
      .unwrap()
      .then((result) => {
        // Force refresh ad status after recording
        return new Promise(resolve => {
          setTimeout(() => {
            dispatch(checkAdStatus(selectedCanvas.id)).then(resolve);
          }, 500); // Small delay to ensure backend processes the record
        });
      })
      .catch((error) => {
        console.error('[AdWidget] Error recording ad view:', error);
      })
      .finally(() => {
        setIsProcessing(false);
        setShowAdModal(false);
        setCurrentAd(null);
        setAdWatchTime(0);
        setAdLoaded(false);
        setAdRecorded(false);
      });
  };

  const handleAdClickThrough = () => {
    if (!currentAd || isProcessing || adRecorded) return;

    const clickTarget = currentAd.clickUrl || DEFAULT_CLICK_URL;
    if (clickTarget) {
      window.open(clickTarget, '_blank', 'noopener,noreferrer');
    }
    handleAdComplete('clicked');
  };

  const handleAdSkip = () => {
    if (!currentAd || isProcessing) {
      console.warn('[AdWidget] Skip blocked:', { currentAd: !!currentAd, isProcessing });
      return;
    }
    
    if (!selectedCanvas?.id) {
      console.error('[AdWidget] Canvas ID missing, cannot skip ad');
      return;
    }
    
    setIsProcessing(true);
        
    dispatch(recordAdView({
      adId: currentAd.id,
      status: 'skipped',
      durationWatched: adWatchTime,
      selectedCanvasId: selectedCanvas?.id
    }))
      .unwrap()
      .catch((error) => {
        console.error('[AdWidget] Error skipping ad:', error);
      })
      .finally(() => {
        setIsProcessing(false);
        setShowAdModal(false);
        setCurrentAd(null);
        setAdWatchTime(0);
        setAdLoaded(false);
        setAdRecorded(false);
      });
  };

  // Premium users
  if (adStatus?.isPremium) {
    return (
      <div className="flex items-center justify-center py-2 bg-gradient-to-r from-yellow-100 to-yellow-200 rounded-md shadow-sm">
        <FiAward className="text-yellow-500 mr-2" />
        <span className="text-sm font-medium text-gray-700">Premium Account - Full Access</span>
      </div>
    );
  }

  // Unlocked state
  if (adStatus?.hasAccess) {
    return (
      <div className="flex items-center justify-between py-2 px-4 bg-gradient-to-r from-green-50 to-green-100 rounded-md shadow-sm border border-green-200">
        <div className="flex items-center">
          <FiUnlock className="text-green-600 mr-2" />
          <span className="text-sm font-medium text-gray-700">Access Unlocked</span>
        </div>
        <div className="flex items-center">
          <FiClock className="text-gray-500 mr-1" />
          <span className="text-sm text-gray-600">
            {adStatus.remainingMinutes} min remaining
          </span>
        </div>
      </div>
    );
  }

  // Locked state
  const adsLeft = Math.max(0, adStatus.adsRequired - adStatus.adsWatched);
  const progress = adStatus.adsRequired > 0
    ? ((adStatus.adsWatched / adStatus.adsRequired) * 100)
    : 0;

  return (
    <>
      <div className="flex flex-col md:flex-row items-center justify-between py-4 px-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg shadow border border-gray-200">
        <div className="flex items-center mb-2 md:mb-0">
          <FiLock className="text-gray-500 mr-3 text-xl" />
          <span className="text-base font-semibold text-gray-700">
            {adsLeft > 0
              ? <>Watch <span className="text-btn-primary font-bold">{adsLeft}</span> more ad{adsLeft !== 1 ? 's' : ''} to unlock access for <span className="font-bold">30 minutes</span></>
              : <>Almost there! Complete the ad to unlock access.</>
            }
          </span>
        </div>
        <button
          onClick={handleWatchAdsClick}
          disabled={adViewInProgress || adStatus.loading || showAdModal}
          className="bg-btn-primary hover:bg-btn-primary-hover text-white py-2 px-6 rounded-md flex items-center text-base font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiPlayCircle className="mr-2" />
          Watch Ad
          {(adViewInProgress || adStatus.loading) && (
            <span className="ml-2 h-4 w-4 border-2 border-t-transparent border-white rounded-full animate-spin"></span>
          )}
        </button>
      </div>
      <div className="w-full mt-2 mb-4 px-2">
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-btn-primary h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1 px-1">
          <span>{adStatus.adsWatched} watched</span>
          <span>{adStatus.adsRequired} required</span>
        </div>
      </div>
      {showAdModal && currentAd && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="max-w-2xl w-full mx-auto bg-white rounded-lg shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gray-800 text-white p-4 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">Advertisement</h3>
                <p className="text-sm text-gray-300">Watch to unlock access for 30 minutes</p>
              </div>
              <button
                onClick={handleAdSkip}
                disabled={isProcessing}
                className="text-gray-400 hover:text-white disabled:opacity-50 transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Google Ad Display */}
            <div className="bg-gray-50 p-6 min-h-[400px] flex items-center justify-center">
              <div
                className="w-full cursor-pointer"
                onClick={handleAdClickThrough}
                role="button"
                aria-label="Open advertisement"
              >
                <GoogleAd
                  slot={currentAd?.slotId || process.env.REACT_APP_AD_SLOT_MAIN}
                  format="auto"
                  responsive={true}
                  dataAdtest={process.env.REACT_APP_ENABLE_ADTEST === 'true'}
                  onAdLoaded={() => setAdLoaded(true)}
                  clickUrl={currentAd?.clickUrl || DEFAULT_CLICK_URL}
                />
                {!adLoaded && (
                  <div className="mt-4 text-center text-gray-500 text-sm">Ad is loading...</div>
                )}
              </div>
            </div>

            {/* Watch Timer and Controls */}
            <div className="bg-gray-100 p-6 border-t">
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Watched: {adWatchTime}s</span>
                  <span className="text-sm text-gray-600">
                    {adWatchTime >= VIEW_SECONDS_REQUIRED 
                      ? 'Ad View Counted ✓' 
                      : `${Math.max(VIEW_SECONDS_REQUIRED - adWatchTime, 0)}s to count view`
                    }
                  </span>
                </div>
                <div className="w-full bg-gray-300 rounded-full h-2.5">
                  <div
                    className="bg-btn-primary h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((adWatchTime / VIEW_SECONDS_REQUIRED) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleAdSkip}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md font-medium disabled:opacity-50 transition-colors"
                >
                  Skip Ad
                </button>
                <button
                  onClick={handleAdComplete}
                  disabled={isProcessing || adWatchTime < VIEW_SECONDS_REQUIRED}
                  className="flex-1 px-4 py-2 text-white bg-btn-primary hover:bg-btn-primary-hover rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isProcessing ? (
                    <span className="flex items-center justify-center">
                      <span className="h-4 w-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></span>
                      Processing...
                    </span>
                  ) : (
                    'Done Watching'
                  )}
                </button>
              </div>
            </div>

            {isProcessing && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10 rounded-lg">
                <div className="text-btn-primary font-semibold">Submitting ad view...</div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AdStatusWidget;